/* eslint-disable max-lines */
/* eslint-disable max-statements */
// eslint-disable-next-line no-use-before-define
import React, {useEffect, useState, useCallback, useRef} from 'react';
import ReactDom from 'react-dom';
import cx from 'classnames';
import {
    NodeViewRendererProps,
    useTransform,
    MeasureResizable,
    BothResizeButtons,
    FloatingBar,
    useCardProps,
    FloatingBarConfig,
    usePresentation,
} from '@baidu/realtime-editor';
import Loadable from 'react-loadable';
import Tooltip from '@/components/Tooltip';

import * as store from '@/store';
import {getKnowledgePageRouteMatchInfo, isInKuShare} from '@/utils/urlUtils';
import PlaceHolderImg from '@/assets/img/flexBuilderPlaceHolder.png';
import {WizKnownError} from '@/sdk/common/error';
import toast, {ToastType} from '@/components/Toast';
import {flexBuilderInstanceObj} from '@/kuTools/utils';
import {flexBuilderClipboard, flexBuilderPaste, copyKvStorageObj, reportCustomErrorToWeirWood} from '../utils';

import {
    createBuilder,
    getBuilderData,
    getBuilderLock,
    getChain,
    getTemplate,
    getTemplateListCache,
    lockBuilder,
    unlockBuilder,
    updateBuilderData,
} from './request';
import {
    DEFAULT_CODE_PREVIEW_COMPONENT_HEIGHT,
    DEFAULT_CODE_PREVIEW_COMPONENT_WIDTH,
    DEFAULT_CUSTOM_INIT_WIDTH,
    FlexBuilderType,
    LogIdMap,
    LogIdMapForBackend,
    NodeTypeOfBackendFlexBuilder,
} from '../constant';
import {CodePreviewComponent, CompiledInfo} from './CodePreviewComponent';
import {PlaceHolder} from './PlaceHolder';
import {StoreProvider} from './store';
import {CustomInit} from './CustomInit';
import AiComponentLogo from '../icon/ai-component-logo.svg?tsx';
import AiComponentLogoForBackend from '../icon/ai-component-logo-for-backend.svg?tsx';
import {Empty, EmptyType} from './Empty';
import type {
    IGetBuilderLockResponse,
    IGetTemplateListResponse,
} from './request.type';
import {debounce} from 'lodash-es';
import {dataBus} from './data-bus';
import {
    generateUUID,
    getBuilderAlive,
    getBuilderAliveAndNoCode,
    getBuilderAliveAndNoCodeAndHasNotEdited,
    getCode,
    getCompiledInfo,
    getDefaultCustomInitHeight,
    getFloatingBarConfigOfCardMode,
    getFloatingBarConfigOfEdit,
    getIAmEditing,
    getIsCard,
    getIsEditing,
    getIsInLayoutContainer,
    getIsShowLogo,
    getTitle,
    openFlexBuilderInstructionDoc,
    getFloatingBarConfigOfNotificationConfig,
} from '../utils';
import {useBuilderEffect} from './hooks/useBuilderEffect';
import {utils as KuUtils} from '@/kuTools/sdk/utils';
import {RepType} from '@/kuTools/types';
import BtnResponsiveHeight from '../icon/btn-responsive-height.svg?tsx';
import {getIsSdkPage} from '@/kuSdk/view/DocViewAll/utils/isSdkPage';
import {
    observeElementVisibility,
    setFlexBuilderStateOfStore,
    unobserveElement,
} from './utils';
import eventBus, {
    CHANGE_FLEX_BUILDER_CARD_STATUS,
    CLICK_FLEX_BUILDER_LOGO,
    EDIT_FLEX_BUILDER,
} from '@/eventBus';
import {VIEW_BLOCK} from '@/hooks/queryViewController/config';
import {isShowCurrentView} from '@/hooks/queryViewController';
import {GRAY_RELEASE_KEYS, initGray} from '@/grayRelease';
import {usePluginCopyEffect} from '@/kuTools/plugins/flexBuilder/render/hooks/usePluginCopyEffect';
import {getEditor} from './MorphoAPIInjector/utils';
import {ApiDoc} from './EditComponent/ApiDoc';
import {CodePreviewProps} from '@baidu/code-live-render';
import {replaceChainId} from './EditComponent/ApiDoc/utils';
import {replaceDomain} from './EditComponent/ApiDocMarkdown/utils';

import styles from './index.module.scss';

const {isInGray: isMatchFlexBuilder} = initGray(GRAY_RELEASE_KEYS.flexBuilder);
const {isInGray: isMatchFlexBuilderLite} = initGray(
    GRAY_RELEASE_KEYS.flexBuilderLite
);

const EditComponent = Loadable({
    loader() {
        return import('./EditComponent');
    },
    loading() {
        return null;
    },
});

function createDiv(id: string) {
    let divEl = document.querySelector(`#${id}`) as HTMLElement;
    if (divEl) {
        return divEl;
    }

    divEl = document.createElement('div');
    divEl.id = id;
    Object.assign(divEl.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        right: '0',
        bottom: '0',
        zIndex: 1000,
        backgroundColor: '#fbfbfb',
    });
    document.body.appendChild(divEl);
    return divEl;
}

interface IPluginAttr {
    builderId: string;
}

const getBuilderLockDebounce = debounce(
    async (builderId: string, setLockInfo: (data: any) => void) => {
        try {
            const data = await getBuilderLock(builderId);
            if (data) {
                setLockInfo(data);
            }
        } catch (error) {
            console.error(error);
        }
    },
    1000,
    {
        leading: true,
        trailing: false,
    }
);

export const renderCompFactory = (flexBuilderType: FlexBuilderType) => {
    // eslint-disable-next-line max-statements
    const RenderComp: React.FC<NodeViewRendererProps<IPluginAttr>> = (
        props: any
    ) => {
        const {
            node,
            isFocused,
            readonly,
            isCard: isCardView,
            parentReadonly,
        } = props;

        const builderId = node.builderId || '';
        const hasEdited = node.hasEdited ?? false;
        const updateKey = node.updateKey ?? '';
        const blockId = node.blockId;

        const {isDocPage} = getIsSdkPage()
            ? {isDocPage: true}
            : getKnowledgePageRouteMatchInfo();
        const hasZoom = node.hasZoom;
        const chainId = node.chainId;
        // 是否需要自适应
        const needResponse = hasZoom === undefined ? true : !hasZoom;
        const userGuid = store.getData(store.KEYS.USER)?.userGuid;

        const [code, setCode] = useState<string>('');
        const [compiledInfo, setCompiledInfo] = useState<CompiledInfo>({
            compiledCode: '',
            dependencies: [],
            style: '',
        });
        const [isFinishInit, setFinishInit] = useState(false);
        const [maxWidth, setMaxWidth] = useState(20000);
        const [isResizing, setIsResizing] = useState(false);
        const [lockInfo, setLockInfo] = useState<IGetBuilderLockResponse>({
            locked: false,
        });
        const [templateListData, setTemplateListData] =
            useState<IGetTemplateListResponse>({categories: [], templates: []});
        const [responsiveHeight, setResponsiveHeight] = useState(0);
        const [apiDocMarkdown, setApiDocMarkdown] = useState<string>('');
        const [forceUpdateApiDoc, setForceUpdateApiDoc] = useState<number>(0);

        const transform = useTransform();
        const {
            cardType,
            asCardChild,
            setCardType,
            splitCard,
            copyCard,
            deleteCard,
        } = useCardProps();
        const {isPresentation, presentationState} = usePresentation();
        const isPagePresentationMode =
            isPresentation && presentationState?.viewMode === 'page'; // 是否是卡片视图的演示模式
        const isCard = getIsCard({cardType, asCardChild}); // 是否为随心搭卡片
        const isInLayoutContainer = getIsInLayoutContainer(asCardChild); // 是否在布局容器内
        const containerRef = useRef<HTMLDivElement>(null);
        const hasLogShowComponent = useRef<boolean>(false);
        const creatorUidRef = useRef<string | undefined>(undefined);
        const hasUpdateApiDocForInit = useRef<boolean>(false);

        // 是否正在编辑中
        const isEditing = getIsEditing(lockInfo);
        // 是否是我正在编辑
        const iAmEditing = getIAmEditing(userGuid, lockInfo);
        // 是否在 ku 享模式下
        const isKuShareMode = Boolean(isInKuShare());
        // 代码编辑器已生成
        const builderAlive = getBuilderAlive(builderId);
        // 代码编辑器已生成，且组件有代码
        const builderAliveAndHasCode = Boolean(builderAlive && code);
        // 代码编辑器已生成，且组件没有代码
        const builderAliveAndNoCode = getBuilderAliveAndNoCode(
            builderId,
            Boolean(code)
        );
        // 代码编辑器已生成，且组件没有代码，且已经被编辑过
        const builderAliveAndNoCodeAndHasEdited =
            builderAliveAndNoCode && hasEdited;
        // 代码编辑器已生成，且组件没有代码，且没有被编辑过
        const builderAliveAndNoCodeAndHasNotEdited =
            getBuilderAliveAndNoCodeAndHasNotEdited(
                builderId,
                Boolean(code),
                hasEdited
            );
        // 是否显示 JSX 渲染器组件
        const isShowCodePreviewComponent = builderAliveAndHasCode;
        // 获取默认的模板列表组件高度
        const defaultCustomInitHeight = getDefaultCustomInitHeight(
            isCard && isCardView
        );

        // 更新全局存储的状态
        setFlexBuilderStateOfStore(builderId, {
            readonly,
            cardProps: {cardType, asCardChild},
        });

        // 在每次同步组件 lockInfo 时，同时也要更新全局存储的状态
        const setLockInfoWrap = useCallback((lockInfo: IGetBuilderLockResponse) => {
            setLockInfo(lockInfo);

            if (!builderId) {
                return;
            }
            setFlexBuilderStateOfStore(builderId, {lockInfo});
        }, [builderId]);

        const getBuilderLockDebounceWrap = useCallback(
            getBuilderLockDebounce,
            []
        );
        getBuilderLockDebounceWrap(builderId, setLockInfoWrap);

        useBuilderEffect(
            builderId,
            readonly,
            flexBuilderPaste.isExist(builderId)
        );

        useEffect(() => {
            return () => {
                if (builderId) {
                    flexBuilderInstanceObj.delete(builderId);
                }
            };
        }, [builderId]);

        // 在每次同步组件 code 时，同时也要更新全局存储的状态
        const setCodeWrap = useCallback((code: string) => {
            setCode(code);

            if (!builderId) {
                return;
            }
            setFlexBuilderStateOfStore(builderId, {hasCode: Boolean(code)});
        }, [builderId]);

        // 通过浏览器插件来支持跨环境复制随心搭
        const {usePluginCopy} = usePluginCopyEffect(node, transform, flexBuilderType, chainId);

        useEffect(() => {
            if (!isDocPage || usePluginCopy) {
                return;
            }

            const getBuilderInfo = async (id: string) => {
                try {
                    const res = await getBuilderData(id);

                    const newCode = getCode(res);
                    const newCompiledInfo = getCompiledInfo(res);

                    setCodeWrap(newCode);
                    setCompiledInfo(newCompiledInfo);
                    creatorUidRef.current = res?.createdBy;

                    if (res.lockInfo) {
                        setLockInfoWrap(res.lockInfo);
                    }

                    transform.withoutSaving(() => {
                        transform.commands.setNode(node, {
                            title: getTitle(res),
                        });
                    });

                    flexBuilderInstanceObj.set(res?.builderId, {
                        builderId: res?.builderId,
                        title: getTitle(res),
                        blockId: node.blockId,
                        createTime: res?.createdAt,
                    });
                } catch (error) {
                    console.error(error);
                }
            };

            const initBuilder = async () => {
                const docGuid =
                    store.getData(store.KEYS.CUR_DOCUMENT)?.docGuid || '';

                try {
                    if (builderId) {
                        if (flexBuilderPaste.isExist(builderId)) {
                            // 粘贴场景处理：flexBuilderPaste 粘贴对象中存在该节点
                            flexBuilderPaste.delete(builderId);

                            // === 同文档剪切首次粘贴场景 ===
                            // 同文档首次剪切粘贴：无需创建新 builder，直接复用旧的
                            // 若当前文档中不存在相同 builderId，则进入首次粘贴逻辑
                            const clipboardObj = flexBuilderClipboard.get();
                            if (
                                clipboardObj &&
                                clipboardObj.builderId &&
                                clipboardObj.docGuid === docGuid &&
                                !clipboardObj.isPasted &&
                                !flexBuilderInstanceObj.get(
                                    clipboardObj.builderId
                                )
                            ) {
                                // 首次剪切粘贴埋点上报
                                KuUtils.kuLogger({
                                    repId: flexBuilderType === FlexBuilderType.backend
                                        ? LogIdMapForBackend.pasteComponent
                                        : LogIdMap.pasteComponent,
                                    params: {
                                        builderId,
                                        action: 'cut',
                                        type: 'first',
                                    },
                                });

                                flexBuilderClipboard.finishFirstPaste();
                                await getBuilderInfo(builderId);
                                return;
                            }

                            // === 复制或剪切-再次粘贴场景 ===
                            const keepStorages = copyKvStorageObj.has(builderId); // 复制是否携带 KV 数据
                            const res = await createBuilder({
                                docGuid,
                                builderId,
                                keepStorages,
                                flexBuilderType,
                            });

                            if (res?.builderId) {
                                transform.withoutSaving(() => {
                                    transform.commands.setNode(node, {
                                        builderId: res.builderId,
                                        title: getTitle(res),
                                    });
                                });

                                flexBuilderInstanceObj.set(res?.builderId, {
                                    builderId: res?.builderId,
                                    title: getTitle(res),
                                    blockId: node.blockId,
                                    createTime: res?.createdAt,
                                });
                            }

                            // === 埋点上报 ===
                            // 除首次剪切粘贴外，其他粘贴场景埋点上报
                            // 剪切：区分首次/后续；复制：不上报首次/后续
                            KuUtils.kuLogger({
                                repId: flexBuilderType === FlexBuilderType.backend
                                    ? LogIdMapForBackend.pasteComponent
                                    : LogIdMap.pasteComponent,
                                params: {
                                    builderId,
                                    action: clipboardObj ? 'cut' : 'copy',
                                    type: clipboardObj ? 'followup' : '',
                                },
                            });
                        } else {
                            // 已有 builderId，直接 GET 获取内容
                            await getBuilderInfo(builderId);
                        }
                    } else {
                        const templateId = dataBus.getData();

                        // 没有 builderId，则进行创建
                        const res = await createBuilder({
                            docGuid,
                            templateId,
                            flexBuilderType,
                        });

                        if (res?.builderId) {
                            transform.withoutSaving(() => {
                                transform.commands.setNode(node, {
                                    builderId: res.builderId,
                                    title: getTitle(res),
                                });
                            });

                            flexBuilderInstanceObj.set(res?.builderId, {
                                builderId: res?.builderId,
                                title: getTitle(res),
                                blockId: node.blockId,
                                createTime: res?.createdAt,
                            });
                        }
                    }
                } catch (err) {
                    console.error(err);
                } finally {
                    setFinishInit(true);
                }
            };

            initBuilder();
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [builderId, updateKey, usePluginCopy]);

        // 获取模板列表数据
        useEffect(() => {
            getTemplateListCache().then((data) => {
                setTemplateListData(data);
            });
        }, []);

        // 组件展示埋点：要求是已发布过的组件才埋点，目前可以通过是否有代码来判断是否已发布过。同时，要求组件出现在视口内时才上报
        useEffect(() => {
            const containerDOM = containerRef.current;
            if (code && !hasLogShowComponent.current && containerDOM) {
                observeElementVisibility(containerDOM, (isVisible) => {
                    if (code && !hasLogShowComponent.current && isVisible) {
                        if (flexBuilderType === FlexBuilderType.frontend) {
                            KuUtils.kuLogger({
                                repId: LogIdMap.ShowComponent,
                                params: {
                                    builderId,
                                    readonly,
                                    creatorUid: creatorUidRef.current ?? undefined,
                                },
                                type: RepType.Show,
                            });
                        }
                        hasLogShowComponent.current = true;
                    }
                });
            }
            return () => {
                if (containerDOM) {
                    unobserveElement(containerDOM);
                }
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [code, containerRef.current]);

        const handlePublish = async (
            builderId: string,
            newCode: string,
            newCompiledInfo: CompiledInfo,
            templateId?: string
        ) => {
            setCodeWrap(newCode);
            setCompiledInfo(newCompiledInfo);

            try {
                await updateBuilderData(
                    builderId,
                    {
                        code: newCode,
                        compiledCode: newCompiledInfo.compiledCode,
                        dependencies: newCompiledInfo.dependencies,
                        style: newCompiledInfo.style,
                    },
                    templateId
                );

                // 发出协同消息，触发协作者界面渲染
                transform.withoutSaving(() => {
                    transform.commands.setNode(node, {
                        updateKey: generateUUID(8),
                    });
                });
            } catch (error) {
                if (error instanceof WizKnownError) {
                    const {returnCode} = error.toResult();

                    if (returnCode === 503) {
                        toast({
                            type: ToastType.Error,
                            content: '代码量超过限制，请简化代码后重试',
                            duration: 3000,
                        });
                    }
                }
            }
        };

        const handleLockFail = () => {
            console.error(new Error('随心搭组件创建锁失败'));
            toast({
                type: ToastType.Error,
                content: '当前网络出现异常，请刷新页面后重试',
                duration: 3000,
            });
        };

        const handlePublishForEditComponent = useCallback(
            (data: any) => {
                const chainId = data?.chainId;
                if (!chainId) {
                    return;
                }

                transform.commands.setNode(node, {chainId});
            },
            [transform.commands, node]
        );

        const handleEdit = useCallback(async () => {
            // 正在编辑中，且编辑者不是我，则不允许进入编辑状态
            if (isEditing && !iAmEditing) {
                return;
            }

            // 没上锁时，才进行上锁操作
            if (!isEditing) {
                try {
                    const newLockInfo = await lockBuilder(builderId);
                    if (!newLockInfo?.locked) {
                        handleLockFail();
                        return;
                    }
                } catch (error) {
                    handleLockFail();
                    return;
                }

                // 发出协同消息，触发协作者界面渲染
                transform.withoutSaving(() => {
                    transform.commands.setNode(node, {
                        updateKey: generateUUID(8),
                    });
                });
            }

            // 渲染全屏编辑的 DOM
            const dom = createDiv('flex-builder-container');

            // 记录当前组件已经被编辑过
            if (!hasEdited) {
                transform.withoutSaving(() => {
                    transform.commands.setNode(node, {
                        hasEdited: true,
                    });
                });
            }

            const handleDestroy = () => {
                if (dom) {
                    // 主动卸载react组件
                    ReactDom.unmountComponentAtNode(dom);
                    dom.parentNode && dom.parentNode.removeChild(dom);
                }

                // 如果没删除掉，增加兜底方案
                if (dom) {
                    const realDiagramDom = document.querySelector(
                        '#flex-builder-container'
                    );
                    if (realDiagramDom && realDiagramDom.remove) {
                        // 主动卸载react组件(关闭二级页面时，需要移出事件、卸载组件、unmounted等操作)
                        ReactDom.unmountComponentAtNode(dom);
                        realDiagramDom.remove();
                    }
                }

                // 如果解锁失败，暂时按不阻塞主流程来处理，正常退出编辑状态
                unlockBuilder(builderId)
                    .then((data) => {
                        if (data?.unlocked) {
                            getBuilderLockDebounceWrap(
                                builderId,
                                setLockInfoWrap
                            );
                        }
                    })
                    .catch((error) => {
                        const message = '随心搭组件解锁失败';
                        if (error instanceof Error) {
                            error.message = `${message}: ${error?.message}`;
                            console.error(error);
                            return;
                        }
                        console.error(new Error(`${message}: ${error}`));
                    })
                    .finally(() => {
                        // 发出协同消息，触发协作者界面渲染
                        transform.withoutSaving(() => {
                            transform.commands.setNode(node, {
                                updateKey: generateUUID(8),
                            });
                        });
                    });
            };

            const handleApplyCode = (
                newCode: string,
                newCompiledInfo: CompiledInfo
            ) => {
                setCodeWrap(newCode);
                setCompiledInfo(newCompiledInfo);

                // 发出协同消息，触发协作者界面渲染
                transform.withoutSaving(() => {
                    transform.commands.setNode(node, {
                        updateKey: generateUUID(8),
                    });
                });
            };

            ReactDom.render(
                <StoreProvider>
                    <EditComponent
                        onBack={handleDestroy}
                        onApplyCode={handleApplyCode}
                        builderId={builderId}
                        flexBuilderType={flexBuilderType}
                        onPublish={handlePublishForEditComponent}
                        publishedChainId={chainId}
                        blockId={blockId}
                    />
                </StoreProvider>,
                dom
            );
        }, [
            blockId,
            builderId,
            chainId,
            getBuilderLockDebounceWrap,
            handlePublishForEditComponent,
            hasEdited,
            iAmEditing,
            isEditing,
            node,
            setCodeWrap,
            setLockInfoWrap,
            transform,
        ]);

        const onResizeStart = useCallback(() => {
            const dom = containerRef.current;

            if (dom) {
                // 通过属性选择器和类名都能匹配
                const target = dom.closest(
                    '[data-card-target="card-center"].slate-card-center'
                );

                if (target) {
                    const width = target.getBoundingClientRect().width;
                    setMaxWidth(width);
                } else {
                    console.log('没有找到符合条件的父元素');
                }
            }

            setIsResizing(true);
        }, []);

        const onResizeStop = useCallback(
            (evt, {width, height}) => {
                const newProps: Record<string, any> = {
                    boxSize: {width, height},
                };

                if (isShowCodePreviewComponent && !hasZoom) {
                    newProps.hasZoom = true;
                }

                transform.commands.setNode(node, newProps);

                setIsResizing(false);
            },
            [node, transform.commands, isShowCodePreviewComponent, hasZoom]
        );

        const handleCodePreviewComponentSizeChange = ([width, height]: [
            number,
            number]) => {
            if (height !== 0) {
                setResponsiveHeight(height);
            }
        };

        const handleCustomInitTemplate = async (templateId: string) => {
            const template = await getTemplate(templateId);
            if (!template) {
                return;
            }

            const code = getCode(template);
            setCodeWrap(code);
            const compiledInfo = getCompiledInfo(template);
            setCompiledInfo(compiledInfo);
            await handlePublish(builderId, code, compiledInfo, templateId);

            // 记录当前组件已经被编辑过
            if (!hasEdited) {
                transform.withoutSaving(() => {
                    transform.commands.setNode(node, {
                        hasEdited: true,
                    });
                });
            }
        };

        const handleClickContainer = () => {
            transform.focus(node);
        };

        // 卡片视图下，且随心搭卡片状态下，不允许选中状态
        const handleMouseDown = useCallback(
            (e: any) => {
                if (isCard && isCardView) {
                    e.preventDefault();
                }
            },
            [isCard, isCardView]
        );

        const handleResponsive = () => {
            transform.commands.setNode(node, {
                hasZoom: !hasZoom,
            });
        };

        const handleOpenInstruction = useCallback(() => {
            openFlexBuilderInstructionDoc();

            if (flexBuilderType === FlexBuilderType.frontend) {
                KuUtils.kuLogger({
                    repId: LogIdMap.ClickGuidance,
                    params: {
                        readonly,
                        pos: 'hover-logo',
                        type: 'guide',
                    },
                });
            }
        }, [readonly]);

        // 随心搭蒙层点击
        const onMaskClick = useCallback(() => {
            transform.commands.update(node);
            transform.focus(node);
            transform.moveToTextOfNode(node);
        }, [transform, node]);

        const handleCodePreviewError = (error: Error) => {
            reportCustomErrorToWeirWood({
                error,
                builderId,
                prefix: '随心搭渲染错误',
            });
        };

        // 随心搭内部键盘事件
        const handleKeyboardEvent = useCallback<
            NonNullable<CodePreviewProps['onKeyboard']>
        >(
            (key, options) => {
                if (!isPagePresentationMode) {
                    return;
                }
                const editor = getEditor();
                if (!editor) {
                    return;
                }
                switch (key) {
                    case 'ArrowUp':
                        if (
                            options?.atTop ||
                            typeof options?.atTop === 'undefined'
                        ) {
                            editor.MorphoAPI.showPresentationPrevPage(editor);
                        }
                        break;
                    case 'ArrowDown':
                        if (
                            options?.atBottom ||
                            typeof options?.atBottom === 'undefined'
                        ) {
                            editor.MorphoAPI.showPresentationNextPage(editor);
                        }
                        break;
                    case 'ArrowLeft':
                    case 'PageUp':
                        editor.MorphoAPI.showPresentationPrevPage(editor);
                        break;
                    case 'ArrowRight':
                    case 'PageDown':
                        editor.MorphoAPI.showPresentationNextPage(editor);
                        break;
                    case 'Escape':
                        editor.MorphoAPI.exitPresentation(editor);
                        break;
                    default:
                        break;
                }
            },
            [isPagePresentationMode]
        );

        // 随心搭内部滚轮事件
        const handleWheelEvent = useCallback<
            NonNullable<CodePreviewProps['onWheel']>
        >(
            (options) => {
                if (isPagePresentationMode) {
                    return;
                }
                const deltaY = options?.deltaY;
                const docScrollDom = document.querySelector(
                    '.mp-layout-content-container'
                );
                if (
                    !docScrollDom ||
                    typeof deltaY !== 'number' ||
                    typeof docScrollDom.scrollBy !== 'function'
                ) {
                    return;
                }
                docScrollDom.scrollBy({
                    top: deltaY,
                    behavior: 'smooth',
                    left: 0,
                });
            },
            [isPagePresentationMode]
        );

        // 独立成卡 / 取消独立成卡
        const handleCardStatusChange = useCallback(() => {
            if (isCard) {
                setCardType(null);

                KuUtils.kuLogger({
                    repId: flexBuilderType === FlexBuilderType.backend
                        ? LogIdMapForBackend.SplitOrMerge
                        : LogIdMap.SplitOrMerge,
                    params: {operation: 'merge'},
                });
            } else {
                splitCard({cardType: 'full'});

                KuUtils.kuLogger({
                    repId: flexBuilderType === FlexBuilderType.backend
                        ? LogIdMapForBackend.SplitOrMerge
                        : LogIdMap.SplitOrMerge,
                    params: {operation: 'split'},
                });
            }

            // 发出协同消息，触发当前用户、协作者界面渲染
            transform.withoutSaving(() => {
                transform.commands.setNode(node, {updateKey: generateUUID(8)});
            });
        }, [node, isCard, setCardType, splitCard, transform]);

        // 监听编辑随心搭事件
        useEffect(() => {
            const listener = (remoteBuilderId: string) => {
                if (builderId !== remoteBuilderId) {
                    return;
                }
                handleEdit();
            };
            eventBus.on(EDIT_FLEX_BUILDER, listener);
            return () => {
                eventBus.off(EDIT_FLEX_BUILDER, listener);
            };
        }, [builderId, handleEdit]);

        // 监听随心搭独立成卡事件
        useEffect(() => {
            const listener = (remoteBuilderId: string) => {
                if (builderId !== remoteBuilderId) {
                    return;
                }
                handleCardStatusChange();
            };
            eventBus.on(CHANGE_FLEX_BUILDER_CARD_STATUS, listener);
            return () => {
                eventBus.off(CHANGE_FLEX_BUILDER_CARD_STATUS, listener);
            };
        }, [builderId, handleCardStatusChange]);

        // 监听点击随心搭 Logo 事件
        useEffect(() => {
            const listener = (remoteBuilderId: string) => {
                if (builderId !== remoteBuilderId) {
                    return;
                }
                handleOpenInstruction();
            };
            eventBus.on(CLICK_FLEX_BUILDER_LOGO, listener);
            return () => {
                eventBus.off(CLICK_FLEX_BUILDER_LOGO, listener);
            };
        }, [builderId, handleOpenInstruction]);

        // 根据 chain id 获取 markdown 格式的接口文档内容，用于渲染
        const getApiDocMarkdown = async (chainId: string) => {
            const res = await getChain({
                chainId,
            });
            const apiDocMarkdown = replaceDomain(replaceChainId(res?.chainInfo?.description ?? '', chainId));
            setApiDocMarkdown(apiDocMarkdown);
        };

        useEffect(() => {
            if (!chainId) {
                return;
            }
            getApiDocMarkdown(chainId);
        }, [chainId]);

        // 通过监听每次渲染来判断是否已经拿到 containerRef 的值，继而强制渲染一次 ApiDoc，触发 ApiDoc 组件的滚动容器高度计算
        // eslint-disable-next-line react-hooks/exhaustive-deps
        useEffect(() => {
            if (
                hasUpdateApiDocForInit.current === false &&
                containerRef.current
            ) {
                hasUpdateApiDocForInit.current = true;
                setForceUpdateApiDoc(forceUpdateApiDoc + 1);
            }
        });

        if (!isDocPage) {
            return <PlaceHolder />;
        }

        if (!isFinishInit) {
            return null;
        }

        /**
         * 计算缩放容器的高度。考虑 3 种场景：
         * 1. 模板列表界面，使用模板列表的默认高度
         * 2. 需要自适应高度，且此时应该要显示 JSX 渲染器，使用 JSX 渲染器返回的高度
         * 3. 其余场景，使用节点的 boxSize 高度
         */
        const resizeDefaultHeight = builderAliveAndNoCodeAndHasNotEdited
            ? defaultCustomInitHeight
            : needResponse && isShowCodePreviewComponent
                ? responsiveHeight
                : node?.boxSize?.height;

        const heightInCardMode = builderAliveAndNoCodeAndHasNotEdited
            ? defaultCustomInitHeight
            : responsiveHeight || node?.boxSize?.height || 0;

        const showFlexBuilderNormalMode = isShowCurrentView(
            VIEW_BLOCK.flexBuilderShowNormalMode
        );
        const minWidth = showFlexBuilderNormalMode ? 412 : 360;

        return (
            <MeasureResizable
                ratio={null}
                measureType="client"
                minConstraints={[minWidth, 160]}
                maxConstraints={
                    builderAliveAndNoCodeAndHasNotEdited
                        ? [maxWidth, defaultCustomInitHeight]
                        : [maxWidth, 20000]
                }
                defaultWidth={
                    node?.boxSize?.width ??
                    (builderAliveAndNoCodeAndHasNotEdited
                        ? DEFAULT_CUSTOM_INIT_WIDTH
                        : DEFAULT_CODE_PREVIEW_COMPONENT_WIDTH)
                }
                // 兜底策略：如果高度计算结果为 0，使用 boxSize 高度，再则使用 JSX 渲染器组件的默认高度
                defaultHeight={
                    resizeDefaultHeight ||
                    node?.boxSize?.height ||
                    DEFAULT_CODE_PREVIEW_COMPONENT_HEIGHT
                }
                onResizeStart={onResizeStart}
                onResizeStop={onResizeStop}
                useResizeObserver={false}
            >
                {({width, height, renderResizeHandle, measureRef}) => {
                    // 独立成卡 / 取消独立成卡 入口
                    const cardModeConfig = getFloatingBarConfigOfCardMode({
                        builderId,
                        isCard,
                        readonly,
                        isInLayoutContainer,
                        isCardView,
                    });
                    const notificationConfig = getFloatingBarConfigOfNotificationConfig({builderId});

                    // 悬浮菜单配置
                    const middleFloatingBarConfig: FloatingBarConfig[] = [
                        {
                            type: 'copy-button',
                            onClick: (event, defaultHandler) => {
                                // 卡片模式下，且在卡片视图下，才触发卡片的复制
                                if (isCard && isCardView) {
                                    copyCard?.();
                                    return;
                                }

                                // 非卡片模式下，触发节点的复制
                                defaultHandler();

                                KuUtils.kuLogger({
                                    repId: flexBuilderType === FlexBuilderType.backend
                                        ? LogIdMapForBackend.CopyButton
                                        : LogIdMap.CopyButton,
                                    params: {
                                        type: node?.type,
                                        trigger_type: 'copyFromFloatingBar',
                                    },
                                });
                            },
                        },
                        {
                            type: 'button',
                            icon: () => <BtnResponsiveHeight />,
                            title: '高度自适应',
                            hidden: readonly || (isCard && isCardView),
                            className: cx(
                                'mp-btn-responsive',
                                needResponse && 'mp-btn-active'
                            ),
                            onClick: () => handleResponsive(),
                        },
                    ];

                    if (isMatchFlexBuilder() || isMatchFlexBuilderLite()) {
                        middleFloatingBarConfig.push(cardModeConfig);
                        isShowCodePreviewComponent && middleFloatingBarConfig.push(notificationConfig);
                    }

                    const floatingBarConfig: FloatingBarConfig[][] = [
                        [
                            getFloatingBarConfigOfEdit({
                                builderId,
                                isEditing,
                                iAmEditing,
                                isKuShareMode,
                                readonly,
                                lockInfo,
                            }),
                        ],
                        middleFloatingBarConfig,
                        [
                            {
                                type: 'delete-button',
                                hidden: readonly,
                                onClick: (event, defaultHandler) => {
                                    // 卡片模式下，触发卡片的删除
                                    if (isCard) {
                                        deleteCard?.();
                                        return;
                                    }

                                    // 非卡片模式下，触发节点的删除
                                    defaultHandler();
                                },
                            },
                        ],
                    ];

                    return (
                        <div
                            ref={measureRef}
                            className={cx(
                                styles.container,
                                isFocused && styles.focus,
                                isCard && isCardView && styles.isCard,
                                !showFlexBuilderNormalMode && styles.onlyContent
                            )}
                            onClick={handleClickContainer}
                            onMouseDown={handleMouseDown}
                            style={
                                isCard && isCardView
                                    ? {
                                        width: maxWidth,
                                        height: heightInCardMode,
                                    }
                                    : {width, height}
                            }
                            data-builder-id={builderId}
                        >
                            <FloatingBar
                                disable={parentReadonly}
                                trigger={'hover'}
                                placement={'top'}
                                node={node}
                                config={floatingBarConfig}
                            >
                                <div
                                    ref={containerRef}
                                    className={cx(
                                        styles.innerContainer,
                                        !showFlexBuilderNormalMode &&
                                            styles.onlyContent
                                    )}
                                    style={{
                                        height:
                                            isPagePresentationMode && hasEdited && isCard
                                                ? `calc(var(--pt-page-height) / ${presentationState.zoom})`
                                                : isCard && isCardView
                                                    ? heightInCardMode
                                                    : height,
                                    }}
                                >
                                    {/* AI 生成的组件容器的左上角的小图标（文案：生成式组件） */}
                                    {getIsShowLogo({
                                        builderId,
                                        hasCode: Boolean(code),
                                        hasEdited,
                                    })
                                        ? (
                                            <Tooltip
                                                placement={'top'}
                                                content={'点击查看使用说明'}
                                            >
                                                <div
                                                    className={cx(
                                                        styles['ai-component-logo-container'],
                                                        isCard && isCardView && styles.isCard,
                                                        // 用于随心搭卡片页面撑满功能里定位，wiki仓库搜不到代码；此功能后续会下线，找qinyunguo@baidu.com确认
                                                        'ai-component-logo-wrapper'
                                                    )}
                                                    onClick={handleOpenInstruction}
                                                >
                                                    {
                                                        flexBuilderType === FlexBuilderType.backend
                                                            ? <AiComponentLogoForBackend />
                                                            : <AiComponentLogo />
                                                    }
                                                </div>
                                            </Tooltip>
                                        )
                                        : null}
                                    {isShowCodePreviewComponent ? (
                                        flexBuilderType ===
                                        FlexBuilderType.backend
                                            ? (
                                                <ApiDoc
                                                    markdownText={apiDocMarkdown}
                                                    chainId={chainId}
                                                    scene={'preview'}
                                                    apiDocContainerDOM={
                                                        containerRef.current
                                                    }
                                                />
                                            ) : (
                                                <CodePreviewComponent
                                                    readonly={readonly}
                                                    mode={'preview'}
                                                    code={code}
                                                    builderId={builderId}
                                                    blockId={blockId}
                                                    compiledInfo={compiledInfo}
                                                    hScreen={isPagePresentationMode}
                                                    adaptHeight={(needResponse || (isCard && isCardView))
                                                        && !isPagePresentationMode}
                                                    onSizeChange={
                                                        handleCodePreviewComponentSizeChange
                                                    }
                                                    onError={handleCodePreviewError}
                                                    onKeyboard={handleKeyboardEvent}
                                                    onWheel={handleWheelEvent}
                                                    creatorUid={
                                                        creatorUidRef.current
                                                    }
                                                />
                                            )
                                    ) : null}
                                    {/* 自定义生成组件的入口组件 */}
                                    {builderAliveAndNoCodeAndHasNotEdited &&
                                        !readonly &&
                                        !isKuShareMode && (
                                        <CustomInit
                                            templateListData={
                                                templateListData
                                            }
                                            builderId={builderId}
                                            isCard={isCard}
                                            isCardView={isCardView}
                                            flexBuilderType={
                                                node?.type ===
                                                    NodeTypeOfBackendFlexBuilder
                                                    ? FlexBuilderType.backend
                                                    : FlexBuilderType.frontend
                                            }
                                            handleCustom={handleEdit}
                                            handleTemplate={
                                                handleCustomInitTemplate
                                            }
                                        />
                                    )}
                                    {/* 拖拽角标 */}
                                    {!readonly && !(isCard && isCardView) && (
                                        <BothResizeButtons
                                            style={{
                                                display:
                                                    isResizing || isFocused
                                                        ? 'block'
                                                        : undefined,
                                            }}
                                            renderResizeHandle={
                                                renderResizeHandle
                                            }
                                            focus={isFocused}
                                        />
                                    )}
                                    {/* 编辑模式下的遮罩层。编辑模式下需要先点击遮罩层，此时遮罩层消失，然后才能直接操作 iframe 的内容 */}
                                    {readonly ||
                                    (isCard && isCardView) ||
                                    flexBuilderType ===
                                        FlexBuilderType.backend ? null : (
                                            <div
                                                onClick={onMaskClick}
                                                data-morpho-draggable
                                                className={cx(
                                                    styles[
                                                        'iframe-shadow-for-editor-selection-control'
                                                    ],
                                                    'fb-iframe-shadow-drag'
                                                )}
                                                style={{
                                                    display:
                                                    isFocused || readonly
                                                        ? 'none'
                                                        : 'block',
                                                }}
                                            />
                                        )}

                                    {/* 一些空白状态组件 */}
                                    {/* 上锁中，且当前用户不是上锁人 */}
                                    {builderAliveAndNoCodeAndHasEdited &&
                                        isEditing &&
                                        !iAmEditing && (
                                        <Empty
                                            type={EmptyType.Communicating}
                                            textParams={[
                                                lockInfo.nickname ?? '',
                                            ]}
                                        />
                                    )}
                                    {/* 当前无代码，编辑过，当前未上锁 */}
                                    {((builderAliveAndNoCodeAndHasEdited &&
                                        !isEditing) ||
                                        (builderAliveAndNoCodeAndHasNotEdited &&
                                            readonly) ||
                                        (isKuShareMode &&
                                            builderAliveAndNoCodeAndHasNotEdited))
                                            && (
                                                <Empty
                                                    type={
                                                        EmptyType.previewComponentAfterPublish
                                                    }
                                                />
                                            )}
                                    {/* 当前无代码，编辑中，且当前用户是上锁人 */}
                                    {builderAliveAndNoCodeAndHasEdited &&
                                        iAmEditing && (
                                        <Empty type={EmptyType.NotDone} />
                                    )}
                                </div>
                            </FloatingBar>
                        </div>
                    );
                }}
            </MeasureResizable>
        );
    };
    return RenderComp;
};

export const RenderInlineComp: React.FC<NodeViewRendererProps<IPluginAttr>> = (
    props
) => {
    const {node, children} = props;

    return (
        <img
            src={PlaceHolderImg}
            alt=""
            style={{width: '100%', maxWidth: '500px'}}
        />
    );
};
